import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { newsList } from "@/mocks/news";
import type { NewsItem } from "@/types";

const news = newsList as NewsItem[];

export default function NewsDetail() {
  const { id } = useParams();
  const article = news.find((n) => n.id === id);

  usePageMeta({
    title: article ? `${article.title}-影视资讯-四虎视频` : "资讯详情-影视资讯-四虎视频",
    description: article
      ? article.summary
      : "四虎视频影视资讯详情页，为你呈现最新影视新闻、影评与预告的完整内容。",
    keywords: article
      ? `${article.tags.join(",")},影视资讯,四虎视频`
      : "影视资讯,四虎视频",
  });

  if (!article) {
    return (
      <div className="mx-auto w-full max-w-[900px] px-4 py-16 md:px-6">
        <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-xl font-semibold text-foreground-900">
            资讯信息暂缺
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            抱歉，未找到该条资讯，它可能已被移除或链接有误。
          </p>
          <Link
            to="/news"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium whitespace-nowrap text-background-50 transition-colors hover:bg-primary-700"
          >
            <i className="ri-arrow-left-line text-base" />
            返回资讯列表
          </Link>
        </div>
      </div>
    );
  }

  const related = news.filter((n) => n.id !== article.id && n.tags.some((t) => article.tags.includes(t))).slice(0, 3);
  const finalRelated = related.length > 0 ? related : news.filter((n) => n.id !== article.id).slice(0, 3);

  return (
    <div className="mx-auto w-full max-w-[1000px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[{ label: "首页", path: "/" }, { label: "影视资讯", path: "/news" }, { label: "正文" }]}
      />

      <article className="mt-5">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
            {article.category}
          </span>
          <span className="text-foreground-500">{article.publishTime}</span>
          <span className="text-foreground-400">· {article.author}</span>
        </div>

        <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
          {article.title}
        </h1>

        <div className="mt-6 overflow-hidden rounded-lg bg-background-200">
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title} 影视资讯图片`}
            className="h-auto max-h-[440px] w-full object-cover object-top"
          />
        </div>

        <div className="mt-8 space-y-5">
          {article.content.map((paragraph, index) => (
            <p key={index} className="text-base leading-relaxed text-foreground-800">
              {paragraph}
            </p>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {article.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
            >
              #{tag}
            </span>
          ))}
        </div>
      </article>

      <div className="mt-10 border-t border-background-200/70 pt-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-xl font-bold text-foreground-950">相关文章推荐</h2>
          <Link
            to="/news"
            className="flex items-center gap-1 text-sm font-medium whitespace-nowrap text-primary-600 transition-colors hover:text-primary-800"
          >
            <i className="ri-arrow-left-line text-base" />
            返回资讯列表
          </Link>
        </div>
        <div className="mt-5 space-y-3">
          {finalRelated.map((item) => (
            <Link
              key={item.id}
              to={`/news/${item.id}`}
              className="group flex items-start gap-4 rounded-lg border border-background-200/70 bg-background-50 p-3 transition-colors hover:border-primary-200 hover:bg-background-100/60"
            >
              <div className="h-20 w-28 shrink-0 overflow-hidden rounded-md bg-background-200">
                <img
                  src={item.cover}
                  alt={`${item.title} 配图`}
                  title={`${item.title} 影视资讯图片`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div>
                <span className="text-xs text-foreground-500">
                  {item.category} · {item.publishTime}
                </span>
                <h3 className="mt-1 line-clamp-2 font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                  {item.title}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}